// @ts-nocheck
import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import socket, { saveCell } from './socket';

// Helpers reused from summary to compute totals and feedback
const _element_re = /\{([^\}]+)\}/igm;
function getElements(formula, cells) {
  let uniformMatch = formula?.match(/uniform\(\/(\d+)\)/);
  if (uniformMatch && cells) {
    return cells.map((c) => c.label);
  }
  const matches = [...(formula || '').matchAll(_element_re)].map((m) => m[1]);
  return matches;
}

// Evaluates a numeric expression (digits, + - * / and parentheses only).
// Templates are user-editable, so we must never hand them to Function()/eval().
function safeEvalArithmetic(expr) {
  if (!/^[\d+\-*/().\s]*$/.test(expr)) {
    throw new Error('Invalid formula: ' + expr);
  }
  let i = 0;
  const skipSpaces = () => { while (i < expr.length && expr[i] === ' ') i++; };
  const parseNumber = () => {
    const start = i;
    while (i < expr.length && /[\d.]/.test(expr[i])) i++;
    if (start === i) throw new Error('Invalid formula: ' + expr);
    return parseFloat(expr.slice(start, i));
  };
  const parseFactor = () => {
    skipSpaces();
    if (expr[i] === '(') {
      i++;
      const v = parseExpr();
      skipSpaces();
      if (expr[i] !== ')') throw new Error('Invalid formula: ' + expr);
      i++;
      return v;
    }
    if (expr[i] === '-') { i++; return -parseFactor(); }
    if (expr[i] === '+') { i++; return parseFactor(); }
    return parseNumber();
  };
  const parseTerm = () => {
    let v = parseFactor();
    skipSpaces();
    while (expr[i] === '*' || expr[i] === '/') {
      const op = expr[i]; i++;
      const rhs = parseFactor();
      v = op === '*' ? v * rhs : v / rhs;
      skipSpaces();
    }
    return v;
  };
  const parseExpr = () => {
    let v = parseTerm();
    skipSpaces();
    while (expr[i] === '+' || expr[i] === '-') {
      const op = expr[i]; i++;
      const rhs = parseTerm();
      v = op === '+' ? v + rhs : v - rhs;
      skipSpaces();
    }
    return v;
  };
  skipSpaces();
  const result = parseExpr();
  skipSpaces();
  if (i !== expr.length) throw new Error('Invalid formula: ' + expr);
  return result;
}

function evalFormula(subFormula, subElements, elementsValue) {
  let uniformMatch = subFormula?.match(/uniform\(\/(\d+)\)/);
  if (uniformMatch) {
    let maxValue = parseInt(uniformMatch[1]);
    let note = 0;
    for (let subEl of subElements) {
      note += parseFloat(elementsValue[subEl] || 0) * 20.0 / maxValue;
    }
    note /= subElements.length || 1;
    return Math.round(note * 100) / 100;
  }
  for (let subEl of subElements) {
    subFormula = (subFormula || '').replaceAll('{'+subEl+'}', parseFloat(elementsValue[subEl] || 0));
  }
  try {
    return Math.round(safeEvalArithmetic(subFormula) * 100) / 100;
  } catch(e) {
    return 0;
  }
}

const EmailInputCell = (props) => {
  const [editing, setEditing] = createSignal(false);
  let emailRef;

  createEffect(() => {
    const val = props.value || '';
    if (!editing() && emailRef) {
      emailRef.value = val;
    }
  });

  return (
    <input
      ref={emailRef}
      class="form-control form-control-sm"
      type="email"
      placeholder="email@unistra.fr"
      value={props.value || ''}
      onFocus={() => {
        setEditing(true);
        socket.emit('project-lock-cell', { project_id: props.project_id, page: props.student, key: '_email' });
      }}
      onBlur={() => {
        setEditing(false);
        socket.emit('project-unlock-cell', { project_id: props.project_id, page: props.student, key: '_email' });
      }}
      onInput={(e) => {
        props.onUpdate(e.currentTarget.value);
      }}
    />
  );
};

export default function ProjectNotifications(props) {
  const [selected, setSelected] = createSignal({}); // studentName -> boolean
  const [cc, setCc] = createSignal('');
  const [subject, setSubject] = createSignal('Évaluation du PFE');
  const [template, setTemplate] = createSignal(`Bonjour {student},
    
Ceci est un email automatique avec votre note et le feedback de votre projet de fin d'études. 

Votre note totale est de {total}/20.

Commentaires :
{feedback}

Cordialement.`);
  const [sending, setSending] = createSignal(false);
  const [result, setResult] = createSignal(null);

  const studentList = createMemo(() => Object.keys(props.project.pages || {}));

  // Automatically initialize all new students as selected
  createEffect(() => {
    const list = studentList();
    setSelected((prev) => {
      const next = { ...prev };
      let updated = false;
      list.forEach((s) => {
        if (next[s] === undefined) {
          next[s] = true;
          updated = true;
        }
      });
      return updated ? next : prev;
    });
  });

  const getEmail = (student) => props.project.pages?.[student]?.['_email']?.value || '';

  // derive top elements and notes similar to summary
  const topElements = createMemo(() => {
    const formula = (props.project.summary_template || '').split('\n').filter(l => l.trim().startsWith('note:'))[0]?.split(':')[1] || '';
    return getElements(formula);
  });

  const notes = createMemo(() => {
    const formula = (props.project.summary_template || '').split('\n').filter(l => l.trim().startsWith('note:'))[0]?.split(':')[1] || '';
    const topEls = getElements(formula);
    const out = {};
    for (const el of topEls) {
      const sec = (props.project.student_template || []).find((e) => e.name == el);
      if (!sec) continue;
      const subFormula = sec.note;
      const subElements = getElements(subFormula, sec.cells);
      for (const student of Object.keys(props.project.pages || {})) {
        const elementsValue = {};
        subElements.forEach((subEl) => {
          elementsValue[subEl] = props.project.pages[student]?.[el+':'+subEl+':note']?.value;
        });
        if (!out[student]) out[student] = {};
        out[student][el] = evalFormula(subFormula, subElements, elementsValue);
      }
    }
    for (const student of Object.keys(props.project.pages || {})) {
      const elementsValue = {};
      topEls.forEach((el) => { elementsValue[el] = out[student]?.[el]; });
      if (!out[student]) out[student] = {};
      out[student]._total = evalFormula(formula, topEls, elementsValue);
    }
    return out;
  });

  const feedbackFor = (student) => {
    let lines = [];
    for (let el of topElements()) {
      if (el === 'Travail') continue;
      const sec = (props.project.student_template || []).find((e) => e.name == el);
      if (!sec) continue;
      const subEls = getElements(sec.note, sec.cells) || [];
      let sectionStarted = false;
      for (let subEl of subEls) {
        const key = `${el}:${subEl}:comment`;
        const v = props.project.pages[student]?.[key]?.value;
        if (v && String(v).trim() !== '') {
          if (!sectionStarted) {
            lines.push(`# ${el}`);
            sectionStarted = true;
          }
          lines.push(`  - ${subEl}: ${String(v)}`);
        }
      }
      if (sectionStarted) lines.push('');
    }
    return lines.join('\n');
  };

  const renderTemplate = (tmpl, studentName) => {
    const total = notes()[studentName]?._total ?? '';
    const feedback = feedbackFor(studentName);
    return tmpl
      .replaceAll('{student}', studentName)
      .replaceAll('{total}', String(total))
      .replaceAll('{feedback}', feedback);
  };

  const recipients = createMemo(() => {
    return studentList().filter(student => selected()[student] && (getEmail(student) || '').trim() !== '');
  });

  const send = () => {
    setSending(true);
    setResult(null);
    const messages = recipients().map(student => ({
      to: getEmail(student),
      username: student,
      subject: subject(),
      body: renderTemplate(template(), student)
    }));
    socket.emit('project-send-emails', { project_id: props.project.id, messages, cc: cc() });
    const onSent = ({project_id, success, error, count}) => {
      if (project_id !== props.project.id) return;
      setSending(false);
      setResult(success ? `Envoyé à ${count} étudiant(s) avec succès.` : `Erreur: ${error}`);
      socket.off('emails-sent', onSent);
    };
    socket.on('emails-sent', onSent);
  };

  return (
    <div class="card-panel">
      <div class="mb-4 pb-2 border-bottom">
        <h2 class="h4 fw-bold text-dark mb-1">Envoi des notifications par email</h2>
        <p class="text-muted small mb-0">Rédigez le message type et personnalisez automatiquement les notes et appréciations envoyées à chaque étudiant.</p>
      </div>

      <div class="row g-4 mb-4">
        {/* Email settings & template */}
        <div class="col-lg-6">
          <div class="p-3 bg-light rounded-3 border mb-3">
            <h6 class="fw-bold mb-3 d-flex align-items-center gap-2">
              <i class="bi bi-envelope-at text-primary"></i>
              Paramètres d'envoi
            </h6>
            <div class="mb-3">
              <label class="form-label fw-semibold small text-muted">Objet du message</label>
              <input class="form-control" value={subject()} onInput={(e) => setSubject(e.currentTarget.value)} />
            </div>
            <div class="mb-0">
              <label class="form-label fw-semibold small text-muted">Copie conforme (CC)</label>
              <input class="form-control" placeholder="enseignant@unistra.fr" value={cc()} onInput={(e) => setCc(e.currentTarget.value)} />
            </div>
          </div>

          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="form-label fw-semibold small text-muted mb-0">Modèle du corps de message</label>
              <div class="d-flex gap-1">
                <button
                  type="button"
                  class="btn btn-xs btn-outline-secondary py-0 px-1.5 small"
                  onClick={() => setTemplate(template() + '{student}')}
                  title="Insérer le nom de l'étudiant"
                >
                  +{'{student}'}
                </button>
                <button
                  type="button"
                  class="btn btn-xs btn-outline-secondary py-0 px-1.5 small"
                  onClick={() => setTemplate(template() + '{total}')}
                  title="Insérer la note totale"
                >
                  +{'{total}'}
                </button>
                <button
                  type="button"
                  class="btn btn-xs btn-outline-secondary py-0 px-1.5 small"
                  onClick={() => setTemplate(template() + '{feedback}')}
                  title="Insérer les appréciations détaillées"
                >
                  +{'{feedback}'}
                </button>
              </div>
            </div>
            <textarea
              class="form-control font-monospace small"
              rows={9}
              value={template()}
              onInput={(e) => setTemplate(e.currentTarget.value)}
            />
          </div>
        </div>

        {/* Live template preview */}
        <div class="col-lg-6">
          <div class="p-3 bg-light rounded-3 border h-100 d-flex flex-column">
            <h6 class="fw-bold mb-2 d-flex align-items-center gap-2">
              <i class="bi bi-eye text-primary"></i>
              Aperçu en direct (Exemple: {studentList()[0] || 'Étudiant'})
            </h6>
            <div class="bg-white p-3 rounded-2 border flex-grow-1 small" style="white-space: pre-wrap; min-height: 220px; font-family: sans-serif;">
              {studentList().length > 0 ? renderTemplate(template(), studentList()[0]) : 'Aucun étudiant dans ce projet.'}
            </div>
          </div>
        </div>
      </div>

      {/* Recipients list */}
      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="fw-bold mb-0">Destinataires ({recipients().length} sélectionné(s))</h5>
          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              onClick={() => {
                const updated = {};
                studentList().forEach(s => updated[s] = true);
                setSelected(updated);
              }}
            >
              Tout cocher
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              onClick={() => {
                const updated = {};
                studentList().forEach(s => updated[s] = false);
                setSelected(updated);
              }}
            >
              Tout décocher
            </button>
          </div>
        </div>

        <div class="table-responsive border rounded-3">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width: 40px;" class="text-center">#</th>
                <th>Étudiant</th>
                <th>Adresse email</th>
                <th>Aperçu personnalisé</th>
              </tr>
            </thead>
            <tbody>
              <For each={studentList()}>
                {(student) => {
                  const checked = () => selected()[student] !== false;
                  return (
                    <tr>
                      <td class="text-center">
                        <input
                          type="checkbox"
                          class="form-check-input"
                          checked={checked()}
                          onInput={(e) => setSelected({ ...selected(), [student]: e.currentTarget.checked })}
                        />
                      </td>
                      <td class="fw-semibold text-dark">
                        <i class="bi bi-person me-1 text-muted"></i>
                        {student}
                      </td>
                      <td style="max-width: 250px;">
                        <EmailInputCell
                          project_id={props.project.id}
                          student={student}
                          value={getEmail(student)}
                          onUpdate={(newEmail) => {
                            saveCell({
                              project_id: props.project.id,
                              page: student,
                              key: '_email',
                              value: newEmail
                            });
                          }}
                        />
                      </td>
                      <td>
                        <details>
                          <summary class="text-primary small cursor-pointer">Voir le message ({notes()[student]?._total ?? '-'} / 20)</summary>
                          <div class="p-2 mt-1 bg-light rounded border small" style="white-space: pre-wrap; max-height: 150px; overflow-y: auto;">
                            {renderTemplate(template(), student)}
                          </div>
                        </details>
                      </td>
                    </tr>
                  );
                }}
              </For>
            </tbody>
          </table>
        </div>
      </div>

      <div class="d-flex align-items-center gap-3">
        <button
          class="btn btn-primary btn-lg shadow-sm d-inline-flex align-items-center gap-2"
          disabled={sending() || recipients().length === 0}
          onClick={send}
        >
          <Show when={sending()} fallback={<i class="bi bi-send-fill"></i>}>
            <div class="spinner-border spinner-border-sm" role="status"></div>
          </Show>
          <span>{sending() ? 'Envoi en cours…' : `Envoyer à ${recipients().length} étudiant(s)`}</span>
        </button>

        <Show when={result()}>
          <div class="alert alert-info py-2 px-3 mb-0 d-flex align-items-center gap-2">
            <i class="bi bi-info-circle-fill"></i>
            <span>{result()}</span>
          </div>
        </Show>
      </div>
    </div>
  );
}
